import type { UploadFile } from "antd"
import type { Dayjs } from "dayjs"

import type { ProjectInvoice } from "../../Project/types/project-invoices.types.tsx"

type NormalizeDate = (
  date: string | Date | Dayjs | null | undefined
) => string | null

export const isInvoiceAmountAbovePayment = (
  invoiceAmount: number | string | null | undefined,
  paymentAmount: number | null | undefined
): boolean =>
  paymentAmount != null &&
  paymentAmount > 0 &&
  invoiceAmount != null &&
  Number(invoiceAmount) > paymentAmount

export const linkedLinesTotal = (linkedLines: Record<string, number>): number =>
  Object.values(linkedLines).reduce(
    (total, amount) => total + (Number(amount) || 0),
    0
  )

export const defaultLinkedLineAmount = (
  balanceRemaining: string | number | null | undefined,
  invoiceAmount: number | null | undefined,
  alreadyLinked: number
): number => {
  const balance = Math.max(Number(balanceRemaining) || 0, 0)
  if (invoiceAmount == null) return balance
  return Math.max(Math.min(balance, invoiceAmount - alreadyLinked), 0)
}

export const linkedLinesExceedInvoice = (
  linkedLines: Record<string, number>,
  invoiceAmount: number | null | undefined
): boolean =>
  invoiceAmount != null && linkedLinesTotal(linkedLines) > invoiceAmount

export const buildInvoiceCreatePayload = (
  values: ProjectInvoice,
  projectId: string,
  normalizeDate: NormalizeDate
): Partial<ProjectInvoice> => ({
  ...values,
  project: projectId,
  service_type: values.service_type || null,
  date_issued: normalizeDate(values.date_issued) ?? undefined,
  due_date: normalizeDate(values.due_date) ?? undefined
})

const EDITABLE_FIELDS = [
  "invoice_number",
  "date_issued",
  "due_date",
  "amount",
  "type",
  "assignee",
  "customer",
  "service_type"
] as const

type EditableField = (typeof EDITABLE_FIELDS)[number]

const comparable = (
  field: EditableField,
  value: unknown,
  normalizeDate: NormalizeDate
): unknown => {
  if (field === "date_issued" || field === "due_date") {
    return normalizeDate(value as string | null | undefined)
  }
  if (field === "amount") {
    return value === null || value === undefined || value === ""
      ? null
      : Number(value)
  }
  return value === undefined || value === "" ? null : value
}

const sameLines = (
  a: Record<string, number>,
  b: Record<string, number>
): boolean => {
  const aKeys = Object.keys(a)
  if (aKeys.length !== Object.keys(b).length) return false
  return aKeys.every((key) => key in b && Number(a[key]) === Number(b[key]))
}

/**
 * The PATCH an edit sends: only what the user moved. Echoing unchanged fields
 * back would resend dates in the account's display format and a `lines`
 * replacement the backend reads as a rewrite of the billing links.
 */
export const buildInvoiceEditPatch = (
  values: Partial<ProjectInvoice>,
  original: Partial<ProjectInvoice>,
  normalizeDate: NormalizeDate,
  linkedLines: Record<string, number>,
  originalLines: Record<string, number>
): Partial<ProjectInvoice> & {
  lines?: Array<{ finance_line: string; amount: string }>
} => {
  const patch: Record<string, unknown> = {}

  EDITABLE_FIELDS.forEach((field) => {
    if (!(field in values)) return
    const next = comparable(field, values[field], normalizeDate)
    const before = comparable(field, original[field], normalizeDate)
    if (next !== before) patch[field] = next
  })

  if (!sameLines(linkedLines, originalLines)) {
    patch.lines = Object.entries(linkedLines).map(([financeLine, amount]) => ({
      finance_line: financeLine,
      amount: String(amount)
    }))
  }

  return patch as Partial<ProjectInvoice>
}

export const linesToAmounts = (
  lines?: Array<{ finance_line: string; amount: string }> | null
): Record<string, number> =>
  Object.fromEntries(
    (lines ?? []).map((line) => [line.finance_line, Number(line.amount) || 0])
  )

export interface UploadOutcome {
  doneUids: string[]
  failedNames: string[]
}

export const partitionUploadResults = (
  pending: UploadFile[],
  results: PromiseSettledResult<unknown>[]
): UploadOutcome => {
  const doneUids: string[] = []
  const failedNames: string[] = []

  pending.forEach((file, index) => {
    if (results[index]?.status === "fulfilled") {
      doneUids.push(file.uid)
    } else {
      failedNames.push(file.name)
    }
  })

  return { doneUids, failedNames }
}
